<!--
  @fileoverview File Message Component - File/Image/Audio/Video Renderer

  Renders file attachments within chat messages including:
  - Image previews in card carousel
  - File download links
  - Audio file links
  - Video file links
  - Message resend functionality for failed uploads

  @component MessageFile
  @displayName File Message

  Features:
  - Image lightbox preview on click
  - File download with proper naming
  - Support for user and agent file messages
  - Native mobile file opening support
  - Resend button for failed file uploads

  @example
  <MessageFile
    :message="fileMessage"
    :button-outline="'primary'"
    :is-user-message="false"
    @setLightBoxImage="openLightbox"
    @sendPostback="handlePostback"
  />

  @vue3migration
  - Converted to script setup pattern
  - Replaced this.$store with Pinia useMainStore()
  - Replaced Vue.set() with direct array assignment (Vue 3 reactivity)
  - Replaced this.$notify with ElNotification
  - Replaced this.$device with inject('device')
-->
<template>
  <div
    class="file-wrapper"
    :class="{
      'justify-center': botMessage,
      'justify-end': !botMessage && isUserMessage,
      'justify-start': !botMessage && !isUserMessage,
    }"
  >
    <div
      class="message-wrapper"
      :class="{
        'message-bubble-left': !isUserMessage && !imageContent,
        'message-bubble-right': isUserMessage && !imageContent,
        'message-image': imageContent,
      }"
    >
      <!-- image preview in card carousel -->
      <div v-if="message.data.content[0].images || message.data.content[0].image">
        <el-card
          v-for="(item, index) in fileUrls"
          :key="index"
          class="file-card h-fit"
        >
          <a class="has-cursor-pointer" @click="$emit('setLightBoxImage', item)">
            <img class="w-full" :src="item" />
          </a>
        </el-card>
      </div>
      <div v-else-if="message.data.content[0].files || message.data.content[0].audio">
        <div v-viewer="viewerOptions" class="cursor-pointer">
          <div v-for="(item, index) in fileUrls" :key="index">
            <a :href="item" target="_blank" class="no-underline">
              <el-icon class="text-[40px]"><Document /></el-icon>
              {{ fileNameFromURL(item) }}
            </a>
          </div>
        </div>
      </div>
      <div
        v-else-if="message.data.content[0].videos"
        v-viewer="viewerOptions"
        class="cursor-pointer"
      >
        <div v-for="(item, index) in fileUrls" :key="index">
          <a v-if="isNative" @click="openFile(item)" class="no-underline">
            <el-icon class="text-[40px]"><VideoCamera /></el-icon>
            {{ fileNameFromURL(item) }}
          </a>
          <a v-else :href="item" target="_blank" class="no-underline">
            <el-icon class="text-[40px]"><VideoCamera /></el-icon>
            {{ fileNameFromURL(item) }}
          </a>
        </div>
      </div>

      <div v-if="!botMessage || (botMessage && !imageContent)" class="text-right">
        <i class="!text-[9px]" v-html="timestamp(message)"></i>
      </div>
    </div>
    <ResendMessageButton
      :isVisible="isUserMessage && !isMessageSent"
      :resendMessage="resendMessage"
      :isDisabled="isResendButtonDisabled"
      :errorMessage="message.errorMessage"
    />
  </div>
</template>

<script setup>
/**
 * @module components/ChatWindow/Message/File
 * @description File attachment renderer with lightbox and download support
 */
import { ref, computed, watch, onMounted, inject } from "vue";
import * as _ from "lodash-es";
import dayjs from "dayjs";
import { ElNotification } from "element-plus";
import { Document, VideoCamera } from "@element-plus/icons-vue";
import "viewerjs/dist/viewer.css";

import ResendMessageButton from "./ResendMessageButton.vue";
import { useMainStore } from "@/stores/main";

/**
 * Component props
 */
const props = defineProps({
  message: {
    type: Object,
    required: true,
  },
  buttonOutline: {
    type: String,
    default: "",
  },
  isUserMessage: {
    type: Boolean,
    default: false,
  },
});

/**
 * Component emits
 */
const emit = defineEmits(["setLightBoxImage", "sendPostback"]);

/**
 * Pinia store instance
 */
const store = useMainStore();

/**
 * Device info via inject (Vue 3 provide/inject pattern)
 */
const device = inject("device");

/**
 * Viewer.js options for image preview
 */
const viewerOptions = {
  inline: false,
  button: true,
  navbar: true,
  title: true,
  toolbar: true,
  tooltip: true,
  movable: false,
  zoomable: true,
  rotatable: false,
  scalable: false,
  transition: false,
  fullscreen: false,
  keyboard: true,
};

/**
 * Component local state
 */
const fileUrls = ref([]);
const isResendButtonDisabled = ref(false);
const isMessageSent = ref(true);

/**
 * Check if this is a bot reply message
 */
const botMessage = computed(() => props.message.type === "reply");

/**
 * Check if message contains image content
 */
const imageContent = computed(
  () => !!(props.message.data.content[0].images || props.message.data.content[0].image)
);

/**
 * Access webchat settings from Pinia store
 */
const settings = computed(() => store.settings);

/**
 * Check if running on native mobile environment
 */
const isNative = computed(() => device?.iOSSDK || device?.isMobileQuery);

/**
 * Generate timestamp string with sender attribution
 *
 * @param {Object} message - Message object with timestamp
 * @returns {string} Formatted timestamp string
 */
function timestamp(message) {
  let source = props.isUserMessage ? "You" : store.settings.name;
  if (message.type === "agent" && store.settings.showAgentName) {
    source = _.get(message, "data.meta.name") || _.get(message, "data.meta.email");
    const organization = store.settings.organizationName;
    if (organization) {
      source += ` from ${organization}`;
    }
  }
  return `${source} at ${dayjs(message.timestamp).format("D MMM, hh:mma")}`;
}

/**
 * Open file in native viewer (for mobile apps)
 *
 * @param {string} url - File URL to open
 */
function openFile(url) {
  const payload = {
    event: "$display",
    data: {
      type: "iframe",
      url,
    },
  };
  emit("sendPostback", payload);
}

/**
 * Extract filename from URL
 *
 * @param {string} item - URL string
 * @returns {string} Extracted filename
 */
function fileNameFromURL(item) {
  if (!item) {
    return "";
  }
  try {
    const url = new URL(item, window.location.origin);
    const blobName = url.searchParams.get("blobName");
    if (blobName) {
      return blobName.split("/").pop() || blobName;
    }
  } catch (_e) {
    // fallback for non-URL strings
  }
  const indexOfQueryString = item.lastIndexOf("?");
  if (indexOfQueryString < 0) {
    const startIndex = item.lastIndexOf("/") + 1;
    const endIndex = item.length;
    return item.slice(startIndex, endIndex);
  } else {
    return item.slice(item.lastIndexOf("/") + 1, item.lastIndexOf("?"));
  }
}

/**
 * Fetch file URLs with authentication
 *
 * For user/agent messages, fetches signed URLs from the server.
 * For bot messages, uses URLs directly.
 */
function fetchFileUrl() {
  const { images, image, audio, files, videos } = _.get(props.message, "data.content[0]", {});
  const content = images || image || audio || files || videos;
  const contentInArray = Array.isArray(content) ? content : [content];
  _.map(contentInArray, (item, index) => {
    const { url = item } = typeof item === "object" ? item : { url: item };
    const userOrAgentTypeMessage =
      props.message.type === "agent" || props.message.type === "message";

    if (userOrAgentTypeMessage) {
      store
        .fetchFileUrl(url)
        .then((response) => {
          const httpRegex = new RegExp("^(http|https)://");
          const containsHttp = httpRegex.test(response.data);
          if (containsHttp) {
            fileUrls.value[index] = response.data;
          } else {
            fileUrls.value[index] = item;
          }
        })
        .catch((error) => {
          ElNotification({
            type: "error",
            title: "Error fetching image/file",
            message: "Please inform adminstrator of this error.",
          });
          fileUrls.value[index] =
            "https://via.placeholder.com/468x60?text=Image%20not%20found%20or%20deleted";
        });
    } else {
      fileUrls.value[index] = item;
    }
  });
}

/**
 * Resend failed file upload
 *
 * Debounced to prevent rapid-fire clicks.
 */
const resendMessage = _.debounce(
  async function () {
    isResendButtonDisabled.value = true;
    await store.resendFile({
      filesUrls: fileUrls.value,
      id: props.message.id,
      timestamp: props.message.timestamp,
    });
  },
  200,
  { leading: true }
);

/**
 * Update message sent status
 *
 * @param {boolean|undefined} status - Send status
 */
function updateMessageStatus(status) {
  isMessageSent.value = status || status === undefined;
  isResendButtonDisabled.value = false;
}

/**
 * Watch message for send status changes
 */
watch(
  () => props.message,
  (newVal) => {
    updateMessageStatus(newVal.sent);
  }
);

/**
 * Fetch file URLs on component mount
 */
onMounted(() => {
  fetchFileUrl();
});
</script>

<style scoped lang="scss">
.file-card :deep(.el-card__body) {
  padding: 0;
  height: fit-content;
  display: block;
}

.file-wrapper {
  display: flex;
  align-items: center;

  img {
    border-radius: 0px;
  }
}

.message-wrapper {
  font-family: var(--keyreply-font-family);
}

.message-bubble-left {
  position: relative;
  padding: 15px;
  border-radius: 15px;
  text-align: left;
  max-width: 70%;
  color: var(--keyreply-agent-text-color) !important;
  background: var(--keyreply-agent-bubble-color);
  --left-bubble-color: var(--keyreply-agent-bubble-color);
  --background: white;

  &:before {
    content: "";
    position: absolute;
    z-index: 2;
    bottom: -2px;
    left: -7px;
    height: 20px;
    border-bottom-right-radius: 16px 14px;
    -webkit-transform: translate(0, -2px);
    border-left: 20px solid var(--left-bubble-color);
  }
  &:after {
    content: "";
    position: absolute;
    z-index: 3;
    bottom: -2px;
    left: 4px;
    width: 26px;
    height: 20px;
    background: var(--background);
    background-repeat: no-repeat;
    border-bottom-right-radius: 10px;
    -webkit-transform: translate(-30px, -2px);
  }
}

.message-bubble-right {
  position: relative;
  padding: 15px;
  max-width: 70%;
  border-radius: 15px;
  text-align: left;
  color: var(--keyreply-user-text-color) !important;
  background: var(--keyreply-user-bubble-color);
  --right-bubble-color: var(--keyreply-user-bubble-color);
  --background: white;

  &:before {
    content: "";
    position: absolute;
    z-index: -1;
    bottom: -2px;
    right: -7px;
    height: 20px;
    border-bottom-left-radius: 16px 14px;
    border-right: 20px solid var(--right-bubble-color);
    -webkit-transform: translate(0, -2px);
  }
  &:after {
    content: "";
    position: absolute;
    z-index: 1;
    bottom: -2px;
    right: -56px;
    width: 26px;
    height: 20px;
    background: var(--background);
    background-repeat: no-repeat;
    border-bottom-left-radius: 10px;
    -webkit-transform: translate(-30px, -2px);
  }
}

.message-image {
  position: relative;
  padding: 0px;
  max-width: 70%;
}
</style>
